import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Gem, User, Box, TrendingUp, TrendingDown, Dices } from 'lucide-react'
import { motion } from 'motion/react'
import { toast } from 'react-hot-toast'
import useAppStore from '../../../store/useAppStore'
import useBattleStore from '../store/useBattleStore'

const BattleCard = ({ battle, hideFinishedStatus = false, shouldAnimate = false }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAppStore()
  const { joinBattle } = useBattleStore()
  const [isJoining, setIsJoining] = useState(false)

  const handleJoin = async (e) => {
    e.stopPropagation()
    
    // Проверяем баланс на фронтенде перед запросом
    if (user.balance < battle.need_balance) {
      toast.error(t('cases.notEnoughCrystals'))
      setTimeout(() => navigate('/payment'), 1000)
      return
    }

    // Отправляем цель в Яндекс.Метрику
    if (window.ym) {
      window.ym(104258304, 'reachGoal', 'join-case-battle')
    }

    try {
      setIsJoining(true)
      
      // Присоединяемся к баттлу
      const result = await joinBattle(battle.id)
      
      // Если НЕ последний игрок (статус waiting)
      if (!result.hasResults) {
        toast.success(t('battle.joinSuccess'))
      }
      
      // Переходим на страницу баттла (с результатами или без)
      // Передаем информацию о том, откуда пришли
      navigate(`/battles/${battle.id}`, { 
        state: { from: hideFinishedStatus ? 'history' : 'battles' } 
      })
      
    } catch (error) {
      // Обработка специфичных ошибок
      if (error.message === 'not_enough_balance') {
        toast.error(t('cases.notEnoughCrystals'))
        setTimeout(() => navigate('/payment'), 1000)
      } else if (error.message === 'error backend') {
        toast.error(t('common.error'))
      } else {
        toast.error(error.message || 'Ошибка присоединения к баттлу')
      }
    } finally {
      setIsJoining(false)
    }
  }

  const handleSpectate = () => {
    // Передаем информацию о том, откуда пришли
    navigate(`/battles/${battle.id}`, { 
      state: { from: hideFinishedStatus ? 'history' : 'battles' } 
    })
  }

  // Можно присоединиться если статус waiting и есть свободные слоты
  // Свободные слоты = count_players - текущее количество игроков
  // НЕ показываем кнопку присоединения если пользователь уже участвует в баттле
  const currentPlayers = battle.players?.length || 0
  const totalSlots = parseInt(battle.count_players) || 2
  const availableSlots = totalSlots - currentPlayers
  const canJoin = battle.status === 'waiting' && availableSlots > 0 && !battle.you

  const BattleTypeIcon = {
    more: TrendingUp,
    less: TrendingDown,
    random: Dices
  }[battle.type]

  return (
    <motion.div
      onClick={handleSpectate}
      initial={
        shouldAnimate
          ? { opacity: 0, x: -20, skewX: 10 }
          : false
      }
      animate={
        shouldAnimate
          ? {
              opacity: [0, 1, 0.8, 1],
              x: [-20, 5, -5, 0],
              skewX: [10, -5, 5, 0]
            }
          : false
      }
      exit={{ opacity: 0, scale: 0.3, filter: 'blur(10px)' }}
      transition={
        shouldAnimate
          ? {
              duration: 0.7,
              times: [0, 0.3, 0.6, 1],
              ease: 'easeOut',
              exit: {
                duration: 0.8,
                ease: [0.34, 1.56, 0.64, 1],
                scale: { type: 'spring', stiffness: 200, damping: 15 }
              }
            }
          : {
              exit: {
                duration: 0.8,
                ease: [0.34, 1.56, 0.64, 1],
                scale: { type: 'spring', stiffness: 200, damping: 15 }
              }
            }
      }
      className={`relative rounded-lg px-3 py-6 border cursor-pointer transition-colors overflow-hidden ${
        battle.you === 1 
          ? 'border-accent-primary hover:border-accent-primary' 
          : 'border-text-secondary/20 hover:border-accent-primary/30'
      }`}
    >
      {/* Background Image */}
      {battle.case?.img && (
        <div 
          className="absolute inset-0 bg-cover opacity-30"
          style={{ 
            backgroundImage: `url(${battle.case.img})`,
            backgroundPosition: 'center 50%'
          }}
        />
      )}
      
      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-dark-primary/0" />
      
      {/* Content */}
      <div className="relative flex flex-col gap-3">
        {/* Первая строка: Иконка кейса + Название */}
        <div className="flex items-center gap-1.5 mb-2">
          <span className="text-white text-sm font-medium truncate lg:text-xl">
            {battle.case?.name}
            {Number(battle.count_case) > 1 && (
              <span className="text-text-secondary ml-1">x{Number(battle.count_case)}</span>
            )}
          </span>
          {BattleTypeIcon && (
            <BattleTypeIcon className="w-4 h-4 text-accent-primary flex-shrink-0 lg:w-6 lg:h-6" />
          )}
        </div>

        {/* Вторая строка: Слоты игроков + Кнопка */}
        <div className="flex items-center justify-between gap-3">
          {/* Player Slots */}
          <div className="flex gap-1.5 lg:gap-3">
            {Array.from({ length: totalSlots }).map((_, index) => {
              const player = battle.players?.[index]
              const isWinner = player?.win === "1" || player?.win === 1
              return (
                <div
                  key={index}
                  className={`
                    w-10 h-10 rounded-full border-2 flex items-center justify-center flex-shrink-0 lg:w-16 lg:h-16
                    ${!player 
                      ? 'border-text-secondary/40 border-dashed bg-dark-primary'
                      : isWinner
                      ? 'border-accent-primary shadow-lg shadow-accent-primary/50'
                      : 'border-text-secondary/40'
                    }
                  `}
                >
                  {player ? (
                    <img
                      src={player.user_ava || `https://ui-avatars.com/api/?name=${player.user_name}&background=00FF7F&color=0A0A0F`}
                      alt={player.user_name}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <User className="w-4 h-4 text-text-secondary/60" />
                  )}
                </div>
              )
            })}
          </div>

          {/* Action Button */}
          <div className="flex-shrink-0">
            {canJoin ? (
              user.balance >= battle.need_balance ? (
                <button
                  onClick={handleJoin}
                  disabled={isJoining}
                  className="px-3.5 py-2 rounded-lg font-semibold text-sm transition-opacity flex items-center gap-1.5 whitespace-nowrap bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary hover:opacity-90 lg:text-lg"
                >
                  {isJoining ? (
                    t('common.loading')
                  ) : (
                    <>
                      <Gem className="w-4 h-4" />
                      {battle.need_balance?.toLocaleString()}
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate('/payment')
                  }}
                  className="px-3.5 py-2 rounded-lg font-semibold text-sm transition-all flex items-center gap-1.5 whitespace-nowrap bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:opacity-90 shadow-blue-500/30 lg:text-lg"
                >
                  <Gem className="w-4 h-4" />
                  +{(battle.need_balance - user.balance).toLocaleString()}
                </button>
              )
            ) : battle.you ? (
              battle.status === 'waiting' ? (
                <div className="px-3 py-2 bg-accent-primary/20 border border-accent-primary/30 text-accent-primary rounded-lg font-semibold text-xs whitespace-nowrap lg:text-lg">
                  {t('battle.yourBattle')}
                </div>
              ) : battle.status === 'raffle' ? (
                <div className="px-3 py-2 bg-accent-primary/20 border border-accent-primary/30 text-accent-primary rounded-lg font-semibold text-xs whitespace-nowrap animate-pulse lg:text-lg">
                  {t('battle.help.step3Title')}
                </div>
              ) : battle.status === 'cancelled' ? (
                <div className="px-3 py-2 bg-orange-900/20 border border-orange-500/30 text-orange-400 rounded-lg font-semibold text-xs whitespace-nowrap lg:text-lg">
                  {t('battle.cancelled')}
                </div>
              ) : battle.status === 'finished' && battle.you_win === 1 ? (
                <div className="px-3 py-2 bg-accent-primary/20 border border-accent-primary/30 text-accent-primary rounded-lg font-semibold text-xs whitespace-nowrap lg:text-lg">
                  🏆 {t('battle.victory')}
                </div>
              ) : battle.status === 'finished' && battle.you_win === 0 ? (
                <div className="px-3 py-2 bg-red-900/20 border border-red-500/30 text-red-400 rounded-lg font-semibold text-xs whitespace-nowrap lg:text-lg">
                  {t('battle.lost')}
                </div>
              ) : null
            ) : battle.status === 'waiting' ? (
              <div className="px-3 py-2 bg-surface border border-accent-primary/30 text-accent-primary rounded-lg font-semibold text-xs cursor-pointer hover:bg-accent-primary/10 transition-colors whitespace-nowrap lg:text-lg">
                {t('battle.watch')}
              </div>
            ) : battle.status === 'raffle' ? (
              <div className="px-3 py-2 bg-surface border border-accent-primary/30 text-accent-primary rounded-lg font-semibold text-xs cursor-pointer hover:bg-accent-primary/10 transition-colors whitespace-nowrap animate-pulse lg:text-lg">
                {t('battle.help.step3Title')}
              </div>
            ) : battle.status === 'cancelled' ? (
              <div className="px-3 py-2 bg-orange-900/20 border border-orange-500/30 text-orange-400 rounded-lg font-semibold text-xs whitespace-nowrap lg:text-lg">
                {t('battle.cancelled')}
              </div>
            ) : battle.status === 'finished' && !hideFinishedStatus ? (
              <div className="px-3 py-2 bg-surface border border-accent-primary/30 text-accent-primary rounded-lg font-semibold text-xs cursor-pointer hover:bg-accent-primary/10 transition-colors whitespace-nowrap lg:text-lg">
                {t('battle.finished')}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </motion.div>
  )
}

export default BattleCard
